// Override controls for `DevPanelActivityDockPeekReadinessProbe`.
// Extracted to keep the probe under the 300-line soft hygiene cap;
// this file owns the controls UI + a tiny `parseAdsToneSelection`
// helper. The TYPE + DEFAULT + storage glue live in
// `src/lib/devTools/peekProbeOverrideStorage.ts` so the probe can
// also read them at the import root without pulling in this whole
// UI file.
//
// Doctrine: the override changes what the probe's own tables read.
// It never touches the live `useActivityDockPeekDecision` — no real
// peek fires and no `ACTIVITY_DOCK_PEEK_EVENT` is recorded.
//
// L2-followup Part C-bugfix change (2026-06-05): when enabled, the
// override now also synthesizes role membership for peek-driving
// roles (full counterfactual). The earlier "respect real roles
// inside hasSignal" doctrine left the probe visually inert for any
// dev who hadn't impersonated — the planning doc's L2-followup
// section captures the design reversal in full.

import type { KpiTone } from "@/components/common/kpi/kpiTypes";
import {
  PEEK_PROBE_OVERRIDE_DEFAULT,
  type PeekProbeSignalOverride,
} from "@/lib/devTools/peekProbeOverrideStorage";

export type SignalOverride = PeekProbeSignalOverride;

const ADS_TONE_OPTIONS: readonly (KpiTone | "null")[] = [
  "null",
  "green",
  "gold",
  "grey",
  "amber",
  "red",
];

function parseAdsToneSelection(value: string): KpiTone | null {
  if (value === "null") return null;
  return value as KpiTone;
}

export function PeekProbeSignalOverrideControls(props: {
  override: SignalOverride;
  setOverride: (
    next: SignalOverride | ((prev: SignalOverride) => SignalOverride),
  ) => void;
}) {
  const { override, setOverride } = props;
  return (
    <details className="mb-2 rounded-lg border border-purple-200 bg-purple-50/50 p-2 text-[11px] dark:border-purple-700/60 dark:bg-purple-900/10">
      <summary className="cursor-pointer font-semibold text-purple-900 dark:text-purple-200">
        Synthetic signal override (probe-only, doesn't fire real peeks)
      </summary>
      <div className="mt-2 space-y-2">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={override.enabled}
            onChange={(e) =>
              setOverride((o) => ({ ...o, enabled: e.target.checked }))
            }
          />
          <span className="text-gray-800 dark:text-zinc-200">
            Use synthesized signal values below instead of the live
            urgency context
          </span>
        </label>
        <div className="flex flex-wrap items-center gap-3 pl-6">
          <label className="flex items-center gap-1.5">
            <span className="text-gray-700 dark:text-zinc-300">
              unsentCount:
            </span>
            <input
              type="number"
              min={0}
              max={999}
              step={1}
              value={override.unsentCount}
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                setOverride((o) => ({
                  ...o,
                  unsentCount: Number.isFinite(n) && n >= 0 ? n : 0,
                }));
              }}
              className="w-16 rounded border border-purple-300 bg-white px-1.5 py-0.5 text-right font-mono text-gray-900 dark:border-purple-700 dark:bg-zinc-800 dark:text-zinc-100"
              disabled={!override.enabled}
            />
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-gray-700 dark:text-zinc-300">
              adsTone:
            </span>
            <select
              value={override.adsTone === null ? "null" : override.adsTone}
              onChange={(e) =>
                setOverride((o) => ({
                  ...o,
                  adsTone: parseAdsToneSelection(e.target.value),
                }))
              }
              className="rounded border border-purple-300 bg-white px-1.5 py-0.5 font-mono text-gray-900 dark:border-purple-700 dark:bg-zinc-800 dark:text-zinc-100"
              disabled={!override.enabled}
            >
              {ADS_TONE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => setOverride(PEEK_PROBE_OVERRIDE_DEFAULT)}
            className="rounded border border-purple-300 bg-white px-2 py-0.5 text-gray-800 dark:border-purple-700 dark:bg-zinc-800 dark:text-zinc-200"
          >
            Reset
          </button>
        </div>
        <p className="pl-6 text-[10px] text-purple-800 dark:text-purple-300">
          The override only changes what the probe's signal + mode-
          projection tables read. The live dock keeps reading the real
          urgency context, so no real peek fires and the
          historical-events panel below stays clean. While enabled,
          the probe ALSO synthesizes role membership for peek-driving
          roles (sales / manufacturing / ads_guidance_editor) so a
          dev viewing the probe sees the full counterfactual without
          having to impersonate. Real roles + assist level are
          additive / unchanged.
        </p>
      </div>
    </details>
  );
}
